import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownList,
  Input
} from '@cokitchen/cokitchen-components';
import { ArrowDownIcon, InfoIcon } from 'cokitchen-icons';
import LocalModal from 'components/base/local-modal';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { Calendar } from 'react-date-range';
import { daysOfTheWeek } from './data';
import { convertTime, convertTo12hrs, timeFrames } from 'utils/functions';
import useOrdersContext from 'contexts/orders.context';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTE_KEYS } from 'utils/constants';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  type?: 'bulk' | 'group' | 'order_details';
  loading?: boolean;
  handleClick: (arg1: {
    recurring: boolean;
    scheduled_delivery_time: string;
    scheduled_delivery_date: string;
    selected_days: string[];
    recurrence: string;
    scheduled: boolean;
    recurring_order_end_date: string;
  }) => void;
};

const ScheduledDeliveryModal = ({
  isOpen,
  onClose,
  handleClick,
  type = 'bulk',
  loading
}: Props) => {
  const { calculatedOrderForm, calculatedOrder } = useOrdersContext();
  const defaultDate = moment().add(4, 'hours').isBefore(moment().set('hour', 18))
    ? moment(new Date()).toDate()
    : moment(new Date()).add(1, 'days').startOf('day').toDate();

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const defaultStartDate =
    calculatedOrderForm.scheduled_delivery_date === ''
      ? defaultDate
      : moment(calculatedOrderForm.scheduled_delivery_date).toDate();

  const defaultEndDate =
    calculatedOrderForm.recurring_order_end_date === ''
      ? moment(new Date()).add(30, 'days').toDate()
      : moment(calculatedOrderForm.recurring_order_end_date).toDate();
  const timeFnc = () => {
    const timeArray = calculatedOrderForm.scheduled_delivery_time?.split(' - ').map((time) => {
      return convertTo12hrs(time);
    });
    return timeArray?.join(' - ');
  };

  const defaultTime = calculatedOrderForm?.scheduled_delivery_time === '' ? '' : timeFnc();

  const [showDateDisplay, setShowDateDisplay] = useState(false);
  const [showTimeDisplay, setShowTimeDisplay] = useState(false);
  const [showFrequencyDisplay, setShowFrequencyDisplay] = useState(false);
  const [showDaysDisplay, setShowDaysDisplay] = useState(false);
  const [showEndDateDisplay, setShowEndDateDisplay] = useState(false);
  const [date, setDate] = useState(defaultStartDate);
  const [time, setTime] = useState(defaultTime);
  const [isRecurrent, setIsRecurrent] = useState(calculatedOrderForm.recurring);
  const [orderFrequency, setOrderFrequency] = useState(calculatedOrderForm.recurrence);
  const [days, setDays] = useState<string[]>(calculatedOrderForm.selected_days);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const formatTime = (time: string) => {
    const timeArray = time.split('-');
    const formattedTime = timeArray.map((item) => convertTime(item));
    return `${formattedTime[0]} - ${formattedTime[1]}`;
  };

  useEffect(() => {
    if (orderFrequency === 'daily') {
      setDays(daysOfTheWeek);
    }
  }, [orderFrequency]);

  const redirectToRestaurants = () => {
    if (pathname === '/orders') {
      navigate(`${ROUTE_KEYS.RESTAURANTS}?type=${type}`);
    } else {
      navigate(`/orders/restaurants?type=group&id=${calculatedOrder?.id}`);
    }
  };
  const isbasketPage = pathname.includes('basket');
  const timeArray = useMemo(() => {
    return timeFrames(date, defaultStartDate, defaultTime, setTime);
  }, [date]);

  const handleClose = () => {
    setTime('');
    onClose();
  };
  return (
    <LocalModal isOpen={isOpen} onRequestClose={handleClose} title='Schedule Delivery'>
      <div className='w-full'>
        <div className='w-full'>
          <p className='text-[#2E3031] text-sm font-medium mb-2'>
            Choose a date for your order to be delivered
          </p>
          <Dropdown
            visible={showDateDisplay}
            onClickOutside={() => setShowDateDisplay(false)}
            disabled={isbasketPage}
            content={
              <div
                className={`w-full flex justify-between h-[52px] bg-[#FAFAFA] py-[15px] px-[14px] border-b-[2px] border-b-[#D5D6D7] ${
                  isbasketPage ? 'cursor-not-allowed' : 'cursor-pointer'
                }`}
                onClick={() => setShowDateDisplay(!showDateDisplay)}>
                <p>{date ? moment(date).format('dddd, Do MMMM YYYY') : 'Select a date'}</p>
                <ArrowDownIcon className='w-5 h-5' />
              </div>
            }>
            <DropdownList className='!p-0 !max-h-fit'>
              <Calendar
                date={date}
                onChange={(date) => setDate(date)}
                showDateDisplay={showDateDisplay}
                className='calendar'
                minDate={defaultDate}
              />
            </DropdownList>
          </Dropdown>
        </div>

        <div className='w-full mt-4'>
          <p className='text-[#2E3031] text-sm font-medium mb-2'>
            Choose a time frame for your order to be delivered
          </p>
          <Dropdown
            visible={showTimeDisplay}
            onClickOutside={() => setShowTimeDisplay(false)}
            disabled={isbasketPage || type === 'order_details'}
            content={
              <div
                className={`w-full flex justify-between h-[52px] bg-[#FAFAFA] py-[15px] px-[14px] border-b-[2px] border-b-[#D5D6D7] ${
                  isbasketPage ? 'cursor-not-allowed' : 'cursor-pointer'
                }`}
                onClick={() => setShowTimeDisplay(!showTimeDisplay)}>
                <p>{time === '' ? 'Select a time frame' : time}</p>
                <ArrowDownIcon className='w-5 h-5' />
              </div>
            }>
            <DropdownList>
              {timeArray.map((timeFrame, index) => (
                <DropdownItem
                  key={index}
                  onClick={() => {
                    setTime(timeFrame);
                    setShowTimeDisplay(false);
                  }}>
                  <p>{timeFrame}</p>
                </DropdownItem>
              ))}
            </DropdownList>
          </Dropdown>
        </div>

        <div className='mt-3 flex gap-1 text-[#96999C] mb-6'>
          <InfoIcon className='mt-[2px]' />
          <p className='text-[#6F7376] text-sm'>
            Ensure to place your order atleast 4 hours before desired delivery date
          </p>
        </div>
        {type === 'bulk' && (
          <Input
            type='checkbox'
            label='Set as recurring order?'
            checked={isRecurrent}
            onChange={() => setIsRecurrent(!isRecurrent)}
            readOnly
          />
        )}
        {isRecurrent && (
          <div className='mt-4'>
            <div className='w-full'>
              <p className='text-[#2E3031] text-sm font-medium mb-2'>
                How often do you want this order?
              </p>
              <Dropdown
                visible={showFrequencyDisplay}
                onClickOutside={() => setShowFrequencyDisplay(false)}
                content={
                  <div
                    className='w-full flex justify-between h-[52px] bg-[#FAFAFA] py-[15px] px-[14px] border-b-[2px] border-b-[#D5D6D7] cursor-pointer'
                    onClick={() => setShowFrequencyDisplay(!showFrequencyDisplay)}>
                    <p className='capitalize'>{orderFrequency ? orderFrequency : 'Select'}</p>
                    <ArrowDownIcon className='w-5 h-5' />
                  </div>
                }>
                <DropdownList>
                  {['daily', 'weekly'].map((frequency) => (
                    <DropdownItem
                      key={frequency}
                      className='capitalize'
                      onClick={() => {
                        setOrderFrequency(frequency);
                        setShowFrequencyDisplay(false);
                      }}>
                      {frequency}
                    </DropdownItem>
                  ))}
                </DropdownList>
              </Dropdown>
            </div>
            {orderFrequency === 'weekly' && (
              <div className='w-full mt-4'>
                <p className='text-[#2E3031] text-sm font-medium mb-2'>
                  Which days do you want this order?
                </p>
                <Dropdown
                  visible={showDaysDisplay}
                  onClickOutside={() => setShowDaysDisplay(false)}
                  content={
                    <div
                      className='w-full flex justify-between h-[52px] bg-[#FAFAFA] py-[15px] px-[14px] border-b-[2px] border-b-[#D5D6D7] cursor-pointer'
                      onClick={() => setShowDaysDisplay(!showDaysDisplay)}>
                      {days.length ? (
                        <div className='flex gap-[2px]'>
                          {days.map((day, index) => (
                            <p key={day}>
                              {index === days.length - 2
                                ? `${day} & `
                                : `${day}${index !== days.length - 1 ? ', ' : ''}`}
                            </p>
                          ))}
                        </div>
                      ) : (
                        <p>Select</p>
                      )}

                      <ArrowDownIcon className='w-5 h-5' />
                    </div>
                  }>
                  <DropdownList>
                    {daysOfTheWeek.map((day) => (
                      <DropdownItem
                        key={day}
                        onClick={() => {
                          if (days.includes(day)) {
                            setDays(days.filter((d) => d !== day));
                          } else {
                            setDays([...days, day]);
                          }
                        }}>
                        <Input type='checkbox' label={day} checked={days.includes(day)} />
                      </DropdownItem>
                    ))}
                  </DropdownList>
                </Dropdown>
              </div>
            )}
            <div className='w-full mt-4'>
              <p className='text-[#2E3031] text-sm font-medium mb-2'>End on</p>
              <Dropdown
                visible={showEndDateDisplay}
                onClickOutside={() => setShowEndDateDisplay(false)}
                content={
                  <div
                    className='w-full flex justify-between h-[52px] bg-[#FAFAFA] py-[15px] px-[14px] border-b-[2px] border-b-[#D5D6D7] cursor-pointer'
                    onClick={() => setShowEndDateDisplay(!showEndDateDisplay)}>
                    <p>
                      {endDate ? moment(endDate).format('dddd, Do MMMM YYYY') : 'Select a date'}
                    </p>
                    <ArrowDownIcon className='w-5 h-5' />
                  </div>
                }>
                <DropdownList>
                  <Calendar
                    date={endDate}
                    onChange={(date) => setEndDate(date)}
                    showDateDisplay={showEndDateDisplay}
                    className='calendar'
                    minDate={moment(new Date()).add(2, 'days').toDate()}
                  />
                </DropdownList>
              </Dropdown>
            </div>
            <div className='mt-3 flex gap-1 text-[#96999C] mb-6'>
              <InfoIcon className='mt-[2px]' />
              <p className='text-[#6F7376] text-sm'>
                All recurrent orders can only last a maximum of 30 days
              </p>
            </div>
          </div>
        )}
        <div className='w-full flex justify-end'>
          <Button
            variant='block'
            color='primary'
            loading={loading}
            onClick={() => {
              handleClick({
                recurring: isRecurrent,
                scheduled_delivery_time: formatTime(time),
                scheduled_delivery_date: moment(date).format('YYYY-MM-DD'),
                selected_days: days,
                recurrence: orderFrequency,
                scheduled: true,
                recurring_order_end_date: moment(endDate).format('YYYY-MM-DD')
              });
              if (!isbasketPage && type !== 'order_details') redirectToRestaurants();
            }}
            className='mt-8 h-[41px] w-[139px]'
            disabled={
              time === '' ||
              (isRecurrent && orderFrequency === '') ||
              (orderFrequency === 'weekly' && days.length === 0)
            }>
            Confirm
          </Button>
        </div>
      </div>
    </LocalModal>
  );
};

export default ScheduledDeliveryModal;
